import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';
import { getPublicTicket, addPublicTicketComment, errorMessage } from '../../services/customerApi';
import { useLanguage } from '../../i18n/LanguageContext';
import { fmtDateTime } from '../../utils/format';
import { API_URL } from '../../services/api';
import { Pill, STATUS_COLORS } from '../../components/NotionUI';

// Passi mostrati nell'avanzamento; "in attesa del cliente" conta come in lavorazione.
const TRACK_STEPS = ['open', 'in_progress', 'resolved', 'closed'];

function PortalTrackTicket() {
  const { t } = useLanguage();
  const { token } = useParams();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [company, setCompany] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPublicTicket(token);
      setTicket(data.ticket);
      setComments(data.comments || []);
      setAttachments(data.attachments || []);
      setCompany(data.company);
      setProject(data.project || null);
    } catch (err) {
      setError(errorMessage(err, t('portal.trackNotFound')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await addPublicTicketComment(token, replyBody.trim());
      setComments(res.comments || []);
      setReplyBody('');
    } catch (err) {
      setError(errorMessage(err, t('portal.replyError')));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="loading">{t('common.loading')}</div>
      </PortalLayout>
    );
  }

  if (!ticket) {
    return (
      <PortalLayout companyName={company?.name}>
        <div className="error-message">{error || t('portal.trackNotFound')}</div>
      </PortalLayout>
    );
  }

  const stepIndex = Math.max(0, TRACK_STEPS.indexOf(ticket.status === 'waiting_customer' ? 'in_progress' : ticket.status));

  return (
    <PortalLayout
      title={t('portal.trackTitle', { id: ticket.id })}
      subtitle={project?.name}
      companyName={company?.name}
      accent={project?.color}
    >
      {error && <div className="error-message">{error}</div>}
      <div className="card portal-gap">
        <div className="card-body">
          <div className="portal-ticket-head">
            <Pill dot color={STATUS_COLORS[ticket.status]}>{t(`portal.status_${ticket.status}`)}</Pill>
            <span className="portal-muted">{t('tickets.createdAt')} {fmtDateTime(ticket.created_at)}</span>
          </div>
          <h2 className="portal-ticket-title">{ticket.subject}</h2>

          <ol className="portal-steps" aria-label={t('portal.statusLabel')}>
            {TRACK_STEPS.map((step, i) => (
              <li key={step} className={'portal-step' + (i < stepIndex ? ' portal-step-done' : '') + (i === stepIndex ? ' portal-step-current' : '')}>
                <span className="portal-step-dot" aria-hidden="true">{i < stepIndex ? '✓' : i + 1}</span>
                <span className="portal-step-label">{t(`portal.status_${step}`)}</span>
              </li>
            ))}
          </ol>

          {ticket.status === 'waiting_customer' && (
            <div className="portal-waiting">
              <strong>{t('portal.waitingTitle')}</strong>
              <span>{t('portal.waitingBody')}</span>
            </div>
          )}

          {project?.csat_enabled && ['resolved', 'closed'].includes(ticket.status) && (
            <div className="portal-waiting portal-rate-cta">
              <strong>{t('portal.rateCtaTitle')}</strong>
              <Link to={`/portal/rate/${token}`}>{t('portal.rateCtaLink')}</Link>
            </div>
          )}

          <p className="portal-ticket-desc">{ticket.description}</p>
        </div>
      </div>

      {attachments.length > 0 && (
        <div className="card portal-gap">
          <div className="card-body">
            <h3 className="portal-section-title">{t('tickets.attachments')} ({attachments.length})</h3>
            <div className="portal-filegrid">
              {attachments.map((a) => (
                <a
                  key={a.id}
                  className="portal-file"
                  href={`${API_URL}/public/tickets/${token}/attachments/${a.id}`}
                  title={a.original_filename}
                >
                  <span className="portal-file-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </span>
                  <span className="portal-file-text">
                    <span className="portal-file-name">{a.original_filename}</span>
                    <span className="portal-file-dl">↓ {t('tickets.download')}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <h3 className="portal-section-title portal-thread-title">{t('portal.conversation')}</h3>
      <div className="portal-thread">
        {comments.length === 0 && <div className="portal-empty">{t('portal.noMessages')}</div>}
        {comments.map((c) => {
          const mine = c.author_type === 'customer';
          const author = mine ? (c.customer_name || t('portal.email')) : (c.agent_name || 'Staff');
          return (
            <div key={c.id} className={'portal-msg' + (mine ? ' portal-msg-mine' : '')}>
              <span className="portal-avatar" aria-hidden="true">{author.trim().charAt(0).toUpperCase()}</span>
              <div className="portal-bubble">
                <div className="portal-bubble-head">
                  <strong>{author}</strong>
                  <span>{fmtDateTime(c.created_at)}</span>
                </div>
                <div className="portal-bubble-body">{c.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={handleReply} className="card portal-gap portal-reply">
        <div className="card-body">
          <div className="form-group">
            <textarea rows={3} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder={t('portal.replyPlaceholder')} />
          </div>
          <button type="submit" className="btn" disabled={sending || !replyBody.trim()}>
            {sending ? t('portal.sending') : t('portal.send')}
          </button>
        </div>
      </form>

      {project?.slug && (
        <div className="portal-register">
          <span>{t('portal.registerHint')}</span>
          <Link to={`/portal/${project.slug}/register`}>{t('portal.registerCta')} →</Link>
        </div>
      )}
    </PortalLayout>
  );
}

export default PortalTrackTicket;
